import React, { ChangeEvent, useEffect, useMemo, useState } from "react"
import { Button, FilterDataSelect } from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { getInitialValues } from './helpers'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { CircularProgress, Grid } from '@mui/material'
import { AutocompleteOption } from 'components/volumetry/interfaces'
import { makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import { useMutation, useQuery } from 'react-query'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { FileUpload } from 'components/core-ui/FileUpload'
import { useOfflineTool } from 'components/core-ui/Offline'
import { AxiosError } from 'axios'
import { getScreenPermissions } from "../../../api"
import { SCREENS } from "../../../constants"

interface ImportProductsProps {
  isModalOpen: boolean
  closeModal: Function
  setShouldRefreshData: Function
  shopCodes: AutocompleteOption[]
  shopTypesOptions: AutocompleteOption[]
  isShopCodesLoading: boolean
  canUseRules: boolean
}

type RegionResponseItem = {
  id: number
  regionName: string
  regionCode: string
}

const ImportProducts: React.FC<ImportProductsProps> = props => {
  const {
    isModalOpen,
    closeModal,
    setShouldRefreshData,
    shopCodes,
    shopTypesOptions,
    isShopCodesLoading,
    canUseRules
  } = props

  const { showNotification } = useNotification()
  const offlineTool = useOfflineTool()
  const [file, setFile] = useState<File | null>(null)

  const { data: permissions } = getScreenPermissions(showNotification, SCREENS.ADMIN_IMPORT_ALL_SHOP_TYPES)

  const initialValues = useMemo(
    () =>
      getInitialValues?.() ?? { shopCodes: [], ruleType: null, regionId: null, file: null },
    [shopCodes, shopTypesOptions]
  )

  const importData = async (values: any) => {
    return await makeCorePostRequest(`/shop-reception/products/sensitives/import`, values, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  }

  const { isLoading, mutate } = useMutation(importData, {
    onSuccess: (resp: any) => {
      showNotification({ message: resp?.result, type: 'success' })
      formik.resetForm()
      setFile(null)
      setShouldRefreshData(true)
      closeModal()
    },
    onError: (err: any) => {
      if (err?.code === AxiosError.ERR_NETWORK) {
        showNotification({ message: 'Verificati conexiunea la internet', type: 'error' })

        offlineTool.addOwnTask({
          type: `Produse sensibile`,
          data: err.config.data as FormData,
          url: `/shop-reception/products/sensitives/import`,
          method: 'post'
        })

        closeModal()
        return
      }

      showNotification({
        message: `A aparut o eroare. ${formatErrorMessage(err)}`,
        type: 'error'
      })
    }
  })

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues,
    onSubmit: async (values: any) => {
      const form = new FormData()

      const selectedShopCodes = values.shopCodes?.map((x: AutocompleteOption) => x.value) || []

      if (selectedShopCodes.length > 0) {
        form.append('shopCodes', selectedShopCodes as any)
      }

      if (canUseRules) {
        const selectedRule = values.ruleType?.value || null
        const selectedRegionId = values.regionId?.value || null

        if (selectedShopCodes.length > 0) {
          form.append('shopCodes', selectedShopCodes as any)
        }

        if (selectedRule) {
          form.append('ruleType', [selectedRule] as any)

          if (selectedRule === 'REGION') {
            form.append('regionId', String(selectedRegionId || ''))
          }
        }
      }

      form.append('file', file as any)
      mutate(form)
    }
  })

  useEffect(() => {
    if (!canUseRules && isModalOpen) {
      formik.setFieldValue('ruleType', null)
      formik.setFieldValue('regionId', null)
    }
  }, [canUseRules, isModalOpen])

  const selectedRuleValue = formik.values.ruleType?.value
  const shouldShowRegions = selectedRuleValue === 'REGION'

  const { data: regionsRaw = [], isLoading: isRegionsLoading } = useQuery(
    ['sensitive-regions'],
    async () => {
      const res = await makeCoreGetRequest(`/shop-reception/products/sensitives/regions`)
      return (res || []) as RegionResponseItem[]
    },
    {
      enabled: !!shouldShowRegions && isModalOpen,
      onError: err =>
        showNotification({
          message: `Nu s-au putut incarca regiunile. ${formatErrorMessage(err)}`,
          type: 'error'
        })
    }
  )

  const regionOptions: AutocompleteOption[] = useMemo(() => {
    return regionsRaw.map(r => ({
      value: r.id,
      label: r.regionName
    }))
  }, [regionsRaw])

  const hasShopsSelected = (formik.values.shopCodes?.length || 0) > 0
  const hasRuleSelected = !!formik.values.ruleType

  const handleSelectShopCodes = (shops: AutocompleteOption[]) => {
    formik.setFieldValue('shopCodes', shops)

    if (shops?.length > 0) {
      formik.setFieldValue('ruleType', null)
      formik.setFieldValue('regionId', null)
    }
  }

  const handleSelectRuleType = (selected) => {
    formik.setFieldValue('ruleType', selected)

    if (selected) {
      formik.setFieldValue('shopCodes', [])
    }

    if (selected?.value !== 'REGION') {
      formik.setFieldValue('regionId', null)
    }
  }

  const handleSelectRegion = (regions) => {
    formik.setFieldValue('regionId', regions)
  }

  const onFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (event === null) {
      formik.setFieldValue('file', null)
      setFile(null)
      return
    }

    const uploadedFile = event?.target.files && event?.target.files[0]
    if (!uploadedFile) return

    const validFileType = uploadedFile.type === 'text/csv' || uploadedFile.name.endsWith('.csv')
    if (!validFileType) {
      showNotification({ message: 'Fisierul este invalid!', type: 'error' })
      return
    }

    setFile(uploadedFile)
    formik.setFieldValue('file', uploadedFile)
  }

  useEffect(() => {
    if (isModalOpen) {
      formik.resetForm()
      setFile(null)
    }
  }, [isModalOpen])

  const isRegionInvalid = shouldShowRegions && !formik.values.regionId

  const isSaveDisabled =
    isLoading ||
    isShopCodesLoading ||
    isRegionsLoading ||
    formik.values.file === null ||
    (!hasShopsSelected && !hasRuleSelected) ||
    isRegionInvalid

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      modalTitle={'Importă produse sensibile'}
      modalWidth="50%"
    >
      <Grid container spacing={1}>
        <Grid xs={12} item>
          {isShopCodesLoading ? (
            <CircularProgress color="primary" disableShrink />
          ) : (
            <FilterDataSelect
              key={JSON.stringify(formik.values.shopCodes)}
              name="shopCodes"
              label="Magazin"
              options={shopCodes}
              defaultSelectedData={formik.values.shopCodes}
              onSelect={handleSelectShopCodes}
              placeholderButton="Selecteaza"
              multiSelect={true}
              showSearch
            />
          )}
        </Grid>

        {canUseRules && permissions?.canImportAllShopsAndAllShopTypes && (
          <Grid xs={12} item>
            <FilterDataSelect
              key={JSON.stringify(formik.values.ruleType)}
              name="ruleType"
              label="Regula"
              options={shopTypesOptions}
              defaultSelectedData={formik.values.ruleType ? [formik.values.ruleType] : []}
              onSelect={handleSelectRuleType}
              placeholderButton="Selecteaza"
              multiSelect={false}
              showSearch
            />
          </Grid>
        )}

        {canUseRules && permissions?.canImportAllShopsAndAllShopTypes && shouldShowRegions && (
          <Grid xs={12} item>
            {isRegionsLoading ? (
              <CircularProgress color="primary" disableShrink />
            ) : (
              <FilterDataSelect
                key={JSON.stringify(formik.values.regionId)}
                name="regionId"
                label="Regiune"
                options={regionOptions}
                defaultSelectedData={formik.values.regionId ? [formik.values.regionId] : []}
                onSelect={handleSelectRegion}
                placeholderButton="Selecteaza"
                multiSelect={false}
                showSearch
              />
            )}
          </Grid>
        )}

        <Grid xs={12} item>
          <FileUpload
            value={formik.values.file}
            fileInputProps={{ displayImage: file }}
            onChange={onFileUpload}
          />
        </Grid>

        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={() => closeModal()} />
          <Button
            variant="contained"
            label={'Salveaza'}
            onClick={() => formik.handleSubmit()}
            disabled={isSaveDisabled}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default ImportProducts
